import { DateTime } from "luxon";
import type { ActiveCustomTimeGroups, CustomTimeGroup, CustomTimeGroupsConfig, ScheduleRow } from "../types";
import { formatTimeTo12Hour } from "../util/shiftTimeFormat";

export type { ActiveCustomTimeGroups, CustomTimeGroup } from "../types";

export const MAX_CUSTOM_TIME_GROUPS = 12;
const MINUTES_PER_DAY = 24 * 60;
const MILLIS_PER_MINUTE = 60 * 1000;
const MINIMUM_COVERED_MINUTES = 60;
const ADJACENT_DAY_OFFSETS = [-1, 0, 1] as const;
const CLOCK_ANCHOR = { year: 2000, month: 1, day: 1 } as const;
const CUSTOM_TIME_GROUP_KEY_PREFIX = "custom-time-group:";
const CONFIGURED_GROUP_KEY_PREFIX = `${CUSTOM_TIME_GROUP_KEY_PREFIX}configured:`;
/** The Other bucket: shifts that cover no configured group by an hour. */
export const UNMATCHED_GROUP_KEY = `${CUSTOM_TIME_GROUP_KEY_PREFIX}unmatched`;

export interface CustomTimeGroupBand {
	key: string;
	label: string;
	rows: ScheduleRow[];
}

interface BuildCustomTimeGroupBandsArgs {
	rows: ScheduleRow[];
	groups: CustomTimeGroup[];
	timezone: string;
	dayStart: string;
	sortDirection: "asc" | "desc";
	otherLabel: string;
}

const minuteToTime = (minute: number): DateTime =>
	DateTime.fromObject({ ...CLOCK_ANCHOR, hour: Math.floor(minute / 60), minute: minute % 60 }, { zone: "UTC" });

export const formatCustomTimeGroupRange = (group: Pick<CustomTimeGroup, "startMinute" | "endMinute">): string =>
	`${formatTimeTo12Hour(minuteToTime(group.startMinute), undefined)} - ${formatTimeTo12Hour(minuteToTime(group.endMinute), undefined)}`;

const parseDayStartMinute = (dayStart: string): number => {
	const [hourText, minuteText] = dayStart.split(":");
	const hour = Number(hourText);
	const minute = Number(minuteText);
	if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
		throw new Error(`Invalid department day start: ${dayStart}`);
	}
	return hour * 60 + minute;
};

const groupDurationMinutes = (group: CustomTimeGroup): number => {
	const duration = (group.endMinute - group.startMinute + MINUTES_PER_DAY) % MINUTES_PER_DAY;
	// A zero-length group would need zero covered minutes and silently match every shift.
	if (duration === 0) throw new Error(`Custom time group ${group.id} has the same start and end time`);
	return duration;
};

/** The group's clock range on one calendar day; a range ending at or before its start ends the next morning. */
const groupIntervalOnDay = (group: CustomTimeGroup, day: DateTime): { start: DateTime; end: DateTime } => {
	// Both ends are wall-clock times, so a DST night does not move the group's end by an hour.
	const start = day.set({ hour: Math.floor(group.startMinute / 60), minute: group.startMinute % 60 });
	const end = day.set({ hour: Math.floor(group.endMinute / 60), minute: group.endMinute % 60 });
	return { start, end: group.endMinute > group.startMinute ? end : end.plus({ days: 1 }) };
};

const overlapMinutes = (left: { start: DateTime; end: DateTime }, right: { start: DateTime; end: DateTime }): number => {
	const overlapMillis = Math.min(left.end.toMillis(), right.end.toMillis()) - Math.max(left.start.toMillis(), right.start.toMillis());
	return Math.max(0, overlapMillis) / MILLIS_PER_MINUTE;
};

/**
 * A shift belongs to a group when it covers at least an hour of it (all of it, for a group shorter
 * than an hour). The group is tried on the shift's own calendar day and the days either side, so
 * overnight shifts and groups match on wall-clock time without depending on the department day start.
 */
const shiftCoversGroup = (shiftStart: DateTime, shiftEnd: DateTime, group: CustomTimeGroup, timezone: string): boolean => {
	const shift = { start: shiftStart.setZone(timezone), end: shiftEnd.setZone(timezone) };
	const requiredMinutes = Math.min(MINIMUM_COVERED_MINUTES, groupDurationMinutes(group));
	const shiftDay = shift.start.startOf("day");
	return ADJACENT_DAY_OFFSETS.some((offset) => overlapMinutes(shift, groupIntervalOnDay(group, shiftDay.plus({ days: offset }))) >= requiredMinutes);
};

const rowCoversGroup = (row: ScheduleRow, group: CustomTimeGroup, timezone: string): boolean => shiftCoversGroup(row.shiftStart, row.shiftEnd, group, timezone);

const groupTimelineMinute = (group: CustomTimeGroup, dayStartMinute: number): number =>
	(group.startMinute - dayStartMinute + MINUTES_PER_DAY) % MINUTES_PER_DAY;

// Ids are free text (the printout assistant writes them too); encoding keeps the composite section-key
// separator " | " out of the key.
const configuredGroupKey = (group: CustomTimeGroup): string => `${CONFIGURED_GROUP_KEY_PREFIX}${encodeURIComponent(group.id)}`;

const configuredGroupLabel = (group: CustomTimeGroup): string => {
	const customName = group.name.trim();
	return customName.length > 0 ? customName : formatCustomTimeGroupRange(group);
};

export interface CustomTimeGroupBucket {
	key: string;
	rows: ScheduleRow[];
}

interface BucketRowsByCustomTimeGroupArgs {
	rows: ScheduleRow[];
	groups: CustomTimeGroup[];
	timezone: string;
	dayStart: string;
	sortDirection: "asc" | "desc";
}

/**
 * Bucket rows into the configured groups, in timeline order from the department day start.
 *
 * Every group is evaluated independently, so a row covering an hour of two groups lands in both
 * (a 7p–7a shift under Evening 3p–11p and Night 11p–7a). Configured groups are retained when empty;
 * the unmatched bucket (Other) is appended, always last, only when rows cover no group.
 */
export const bucketRowsByCustomTimeGroup = (args: BucketRowsByCustomTimeGroupArgs): CustomTimeGroupBucket[] => {
	const { rows, groups, timezone, dayStart, sortDirection } = args;
	const dayStartMinute = parseDayStartMinute(dayStart);
	const indexedGroups = groups.map((group, index) => ({ group, index }));
	indexedGroups.sort((left, right) => {
		const difference = groupTimelineMinute(left.group, dayStartMinute) - groupTimelineMinute(right.group, dayStartMinute);
		if (difference === 0) return left.index - right.index;
		return sortDirection === "desc" ? -difference : difference;
	});

	const matchedRows = new Set<ScheduleRow>();
	const buckets = indexedGroups.map(({ group }) => {
		const groupRows = rows.filter((row) => rowCoversGroup(row, group, timezone));
		for (const row of groupRows) matchedRows.add(row);
		return { key: configuredGroupKey(group), rows: groupRows };
	});

	const unmatchedRows = rows.filter((row) => !matchedRows.has(row));
	if (unmatchedRows.length > 0) buckets.push({ key: UNMATCHED_GROUP_KEY, rows: unmatchedRows });
	return buckets;
};

/** Heading for every key `bucketRowsByCustomTimeGroup` can produce: the group's name, else its range; Other for unmatched. */
export const buildCustomTimeGroupLabels = (groups: CustomTimeGroup[], otherLabel: string): Map<string, string> => {
	if (!otherLabel) throw new Error("Custom time groups require a translated unmatched-group label");
	const labels = new Map(groups.map((group) => [configuredGroupKey(group), configuredGroupLabel(group)]));
	labels.set(UNMATCHED_GROUP_KEY, otherLabel);
	return labels;
};

export const isCustomTimeGroupKey = (key: string): boolean => key.startsWith(CUSTOM_TIME_GROUP_KEY_PREFIX);

interface ShiftBelongsToCustomTimeGroupKeyArgs {
	shiftStart: DateTime;
	shiftEnd: DateTime;
	key: string;
	groups: CustomTimeGroup[];
	timezone: string;
}

/**
 * Whether a shift that is not a schedule row (time off, a call-out) belongs under a group's key —
 * the same one-hour coverage rule rows use; the unmatched key takes shifts covering no group.
 */
export const shiftBelongsToCustomTimeGroupKey = (args: ShiftBelongsToCustomTimeGroupKeyArgs): boolean => {
	const { shiftStart, shiftEnd, key, groups, timezone } = args;
	if (key === UNMATCHED_GROUP_KEY) return !groups.some((group) => shiftCoversGroup(shiftStart, shiftEnd, group, timezone));
	const group = groups.find((candidate) => configuredGroupKey(candidate) === key);
	if (!group) throw new Error(`Unknown custom time group key: ${key}`);
	return shiftCoversGroup(shiftStart, shiftEnd, group, timezone);
};

interface ResolveActiveCustomTimeGroupsArgs {
	config: CustomTimeGroupsConfig;
	timezone: string;
	dayStart: string;
	otherLabel: string;
}

export const resolveActiveCustomTimeGroups = (args: ResolveActiveCustomTimeGroupsArgs): ActiveCustomTimeGroups | null => {
	const { config, timezone, dayStart, otherLabel } = args;
	if (!config.enabled) return null;
	return { groups: config.groups, timezone, dayStart, labels: buildCustomTimeGroupLabels(config.groups, otherLabel) };
};

/** A Start Time section or tab key's heading; throws for a group key the labels don't know. */
export const customTimeGroupKeyLabel = (key: string, labels: ReadonlyMap<string, string>): string => {
	const label = labels.get(key);
	if (label === undefined) throw new Error(`Missing custom time group label for ${key}`);
	return label;
};

/** Build the configured Shift Time pivot bands: `bucketRowsByCustomTimeGroup` with each bucket's heading. */
export const buildCustomTimeGroupBands = (args: BuildCustomTimeGroupBandsArgs): CustomTimeGroupBand[] => {
	const { otherLabel, ...bucketArgs } = args;
	const labels = buildCustomTimeGroupLabels(args.groups, otherLabel);
	return bucketRowsByCustomTimeGroup(bucketArgs).map((bucket) => ({ ...bucket, label: customTimeGroupKeyLabel(bucket.key, labels) }));
};

export const canAddCustomTimeGroup = (groups: CustomTimeGroup[]): boolean => groups.length < MAX_CUSTOM_TIME_GROUPS;

interface DuplicateCustomTimeGroupRangeArgs {
	groups: CustomTimeGroup[];
	groupId: string;
	startMinute: number;
	endMinute: number;
}

export const isDuplicateCustomTimeGroupRange = (args: DuplicateCustomTimeGroupRangeArgs): boolean =>
	args.groups.some((group) => group.id !== args.groupId && group.startMinute === args.startMinute && group.endMinute === args.endMinute);
